/**
 * Políticas por partição: flush caps, retry budgets, quotas.
 */

import type { QueueItem } from './types';
import { getRuntimeMode } from '../runtime/runtimeMode';
import { recordAdaptiveDecision } from '../telemetry';
import {
  type QueuePartition,
  partitionQueueItems,
  flattenPartitionsInFlushOrder,
  FLUSH_ORDER,
  getQueuePartition,
} from './partitioning';

export { FLUSH_ORDER };

export const PARTITION_FLUSH_MAX_ITEMS: Record<QueuePartition, number> = {
  critical: 10,
  conversion: 10,
  behavior: 6,
  replay: 4,
  system: 2,
};

export const PARTITION_TTL_MS: Record<QueuePartition, number> = {
  critical: 48 * 60 * 60 * 1000,
  conversion: 24 * 60 * 60 * 1000,
  behavior: 6 * 60 * 60 * 1000,
  replay: 12 * 60 * 60 * 1000,
  system: 60 * 60 * 1000,
};

export const PARTITION_MAX_BYTES: Record<QueuePartition, number> = {
  critical: 60_000,
  conversion: 48_000,
  behavior: 32_000,
  replay: 40_000,
  system: 8_000,
};

export const PARTITION_QUEUE_QUOTA_RATIO: Record<QueuePartition, number> = {
  critical: 0.15,
  conversion: 0.25,
  behavior: 0.45,
  replay: 0.1,
  system: 0.05,
};

const partitionRetryWindow = new Map<QueuePartition, { start: number; count: number }>();
const PARTITION_RETRY_BUDGET_PER_MIN: Record<QueuePartition, number> = {
  critical: 120,
  conversion: 80,
  behavior: 40,
  replay: 60,
  system: 10,
};

const RETRY_WINDOW_MS = 60_000;

export function canSpendPartitionRetryBudget(
  partition: QueuePartition,
  now: number
): boolean {
  let w = partitionRetryWindow.get(partition);
  if (!w || now - w.start > RETRY_WINDOW_MS) {
    w = { start: now, count: 0 };
    partitionRetryWindow.set(partition, w);
  }
  return w.count < PARTITION_RETRY_BUDGET_PER_MIN[partition];
}

export function spendPartitionRetryBudget(partition: QueuePartition): void {
  const w = partitionRetryWindow.get(partition);
  if (w) w.count += 1;
}

export function applyPartitionFlushCaps(
  items: QueueItem[],
  cycleMaxItems: number
): QueueItem[] {
  const partitioned = partitionQueueItems(items);
  const out: QueueItem[] = [];
  let used = 0;
  for (const p of FLUSH_ORDER) {
    if (used >= cycleMaxItems) break;
    const bucket = partitioned.get(p) ?? [];
    const cap = Math.min(
      PARTITION_FLUSH_MAX_ITEMS[p],
      cycleMaxItems - used
    );
    out.push(...bucket.slice(0, cap));
    used += Math.min(bucket.length, cap);
  }
  return out;
}

export function enforcePartitionQueueQuotas(
  items: QueueItem[],
  maxItems: number
): QueueItem[] {
  if (items.length <= maxItems) return items;
  const partitioned = partitionQueueItems(items);
  const limits: Record<QueuePartition, number> = {
    critical: Math.ceil(maxItems * PARTITION_QUEUE_QUOTA_RATIO.critical),
    conversion: Math.ceil(maxItems * PARTITION_QUEUE_QUOTA_RATIO.conversion),
    behavior: Math.ceil(maxItems * PARTITION_QUEUE_QUOTA_RATIO.behavior),
    replay: Math.ceil(maxItems * PARTITION_QUEUE_QUOTA_RATIO.replay),
    system: Math.ceil(maxItems * PARTITION_QUEUE_QUOTA_RATIO.system),
  };
  const out: QueueItem[] = [];
  for (const p of FLUSH_ORDER) {
    out.push(...(partitioned.get(p) ?? []).slice(0, limits[p]));
  }
  if (out.length > maxItems) return flattenPartitionsInFlushOrder(partitioned).slice(0, maxItems);
  return out;
}

export function getPartitionPressure(
  partition: QueuePartition,
  items: QueueItem[]
): number {
  if (items.length === 0) return 0;
  const count = items.filter(i => getQueuePartition(i) === partition).length;
  return Math.min(1, count / items.length);
}

const partitionReplayWindow = new Map<QueuePartition, { start: number; count: number }>();
const PARTITION_REPLAY_BUDGET_PER_MIN: Record<QueuePartition, number> = {
  critical: 500,
  conversion: 300,
  behavior: 120,
  replay: 200,
  system: 20,
};

export function canSpendPartitionReplayBudget(
  partition: QueuePartition,
  now: number
): boolean {
  let w = partitionReplayWindow.get(partition);
  if (!w || now - w.start > RETRY_WINDOW_MS) {
    w = { start: now, count: 0 };
    partitionReplayWindow.set(partition, w);
  }
  return w.count < PARTITION_REPLAY_BUDGET_PER_MIN[partition];
}

export function spendPartitionReplayBudget(partition: QueuePartition): void {
  const w = partitionReplayWindow.get(partition);
  if (w) w.count += 1;
}

export function getReplayBudgetMultiplier(partition: QueuePartition): number {
  const mode = getRuntimeMode();
  if (mode !== 'pressure' && mode !== 'degraded') return 1;
  if (partition === 'critical' || partition === 'conversion') return 1;
  return 0.5;
}

export function canSpendEffectiveReplayBudget(
  partition: QueuePartition,
  now: number
): boolean {
  const mult = getReplayBudgetMultiplier(partition);
  if (mult < 1) {
    recordAdaptiveDecision('replay', `throttle_${partition}`);
  }
  let w = partitionReplayWindow.get(partition);
  if (!w || now - w.start > RETRY_WINDOW_MS) {
    w = { start: now, count: 0 };
    partitionReplayWindow.set(partition, w);
  }
  const cap = Math.max(1, Math.floor(PARTITION_REPLAY_BUDGET_PER_MIN[partition] * mult));
  return w.count < cap;
}

export function _testResetPartitionRetryBudgets(): void {
  partitionRetryWindow.clear();
  partitionReplayWindow.clear();
}
